import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { organigrammeAPI } from "../services/api";
import { useDepartementCount } from "../context/DepartementCountContext";
import { useCompanyInfo } from "../hooks/useCompanyInfo";
import { DepartementFormDialog, DeleteDepartementDialog } from "../components/organigramme/DepartementDialogs";
import OrganigrammeSearchBar from "../components/organigramme/OrganigrammeSearchBar";
import OrganigrammeStatsCards from "../components/organigramme/OrganigrammeStatsCards";
import OrganigrammeTreeCard from "../components/organigramme/OrganigrammeTreeCard";

const DEFAULT_STATS = {
  total_departments: 0,
  total_employees: 0,
  niveaux_max: 0,
  budget_total: 0,
  nb_responsables: 0,
  department_with_staff: 0,
  current_staff_total: 0,
  max_staff_total: 0,
  taux_occupation: 0,
  taux_responsabilite: 0,
  budget_repartition: [],
};

// Fonction utilitaire pour compter les départements

const Organigramme = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedDepartement, setSelectedDepartement] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [departementDialog, setDepartementDialog] = useState({
    open: false,
    departement: null,
    mode: "create",
    parent: null,
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, departement: null });
  const [draggedItem, setDraggedItem] = useState(null);

  const [stats, setStats] = useState(DEFAULT_STATS);
  const [expandedInitialized, setExpandedInitialized] = useState(false);
  const companyInfo = useCompanyInfo();

  // === Dérivés société (nom, couleur, logo absolu) ===
  const companyPrimaryColor = companyInfo?.company?.primary_color || "#3B82F6";
  const companyLegalName = companyInfo?.company?.legal_name || companyInfo?.company?.trade_name || "";
  const companyLogoUrl = (() => {
    const url = companyInfo?.company?.logo_url;
    if (!url || typeof url !== "string" || url.trim() === "") return null;
    // En développement: uploads servis depuis backend (localhost:8000)
    // En production: uploads servis par nginx sur même origine
    if (url.startsWith("/uploads/")) {
      return process.env.NODE_ENV === "development"
        ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}${url}`
        : url;
    }
    return url;
  })();

  const normalizeStats = useCallback((rawStats) => {
    const normalized = { ...DEFAULT_STATS };

    if (!rawStats || typeof rawStats !== "object") {
      return normalized;
    }

    const pickNumber = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    normalized.total_departments = pickNumber(
      rawStats.total_departments ?? rawStats.totalDepartments,
      normalized.total_departments,
    );

    normalized.total_employees = pickNumber(
      rawStats.total_employees ?? rawStats.totalEmployees,
      normalized.total_employees,
    );

    normalized.niveaux_max = pickNumber(
      rawStats.niveaux_max ?? rawStats.niveauxMax ?? rawStats.max_level ?? rawStats.maxLevel,
      normalized.niveaux_max,
    );

    normalized.budget_total = pickNumber(
      rawStats.budget_total ?? rawStats.annual_budget_total ?? rawStats.budgetTotal,
      normalized.budget_total,
    );

    normalized.nb_responsables = pickNumber(
      rawStats.nb_responsables ?? rawStats.nbResponsables,
      normalized.nb_responsables,
    );

    normalized.department_with_staff = pickNumber(
      rawStats.department_with_staff ?? rawStats.departmentWithStaff ?? rawStats.dept_with_staff,
      normalized.department_with_staff,
    );

    normalized.current_staff_total = pickNumber(
      rawStats.current_staff_total ?? rawStats.currentStaffTotal,
      normalized.current_staff_total,
    );

    normalized.max_staff_total = pickNumber(
      rawStats.max_staff_total ?? rawStats.maxStaffTotal,
      normalized.max_staff_total,
    );

    normalized.taux_occupation = pickNumber(
      rawStats.taux_occupation ?? rawStats.occupation_rate ?? rawStats.occupationRate,
      normalized.taux_occupation,
    );

    normalized.taux_responsabilite = pickNumber(
      rawStats.taux_responsabilite ?? rawStats.responsibility_rate ?? rawStats.responsibilityRate,
      normalized.taux_responsabilite,
    );

    const repartition = rawStats.budget_repartition ?? rawStats.budgetRepartition;
    if (Array.isArray(repartition)) {
      normalized.budget_repartition = repartition;
    }

    // Expose legacy aliases to avoid breaking older UI expectations
    normalized.dept_with_staff = normalized.department_with_staff;
    normalized.occupation_rate = normalized.taux_occupation;
    normalized.responsibility_rate = normalized.taux_responsabilite;
    normalized.max_level = normalized.niveaux_max;
    normalized.annual_budget_total = normalized.budget_total;

    return normalized;
  }, []);

  const [newDepartement, setNewDepartement] = useState({
    name: "",
    code: "",
    description: "",
    parent_id: null,
    manager_id: null,
    annual_budget: "",
    max_staff: "",
    current_staff: 0,
    order: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef(null);

  const { refreshDepartementStats } = useDepartementCount();

  const [orgQ, empQ] = useQueries({
    queries: [
      {
        queryKey: ["organigramme"],
        queryFn: async () => {
          const response = await organigrammeAPI.getOrganizationChart();
          let statsData = normalizeStats(response.statistics);
          try {
            const detailed = await organigrammeAPI.getOrganizationChartStats();
            if (detailed?.statistics) statsData = normalizeStats(detailed.statistics);
          } catch {}
          return { departements: response.organization_chart || [], stats: statsData };
        },
      },
      {
        queryKey: ["organigramme-employees"],
        queryFn: () =>
          organigrammeAPI.getEmployees().then((r) => (Array.isArray(r) ? r : [])),
      },
    ],
  });

  const departements = orgQ.data?.departements ?? [];
  const employees = empQ.data ?? [];
  const loading = orgQ.isPending;

  useEffect(() => {
    if (orgQ.data) setStats(orgQ.data.stats);
  }, [orgQ.data]);

  useEffect(() => {
    if (orgQ.isError) toast.error(t("common.error"), { description: t("organigramme.toasts.loadError") });
  }, [orgQ.isError]);

  useEffect(() => {
    if (departements.length > 0 && !expandedInitialized) {
      setExpandedNodes(new Set([1]));
      setExpandedInitialized(true);
    }
  }, [departements, expandedInitialized]);

  const refreshOrganigramme = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["organigramme"] });
  }, [queryClient]);

  useEffect(() => {
    if (departementDialog.open && departementDialog.mode === "edit" && departementDialog.departement) {
      setNewDepartement({
        name: departementDialog.departement.name || "",
        code: departementDialog.departement.code || "",
        description: departementDialog.departement.description || "",
        parent_id: departementDialog.departement.parent_id || null,
        manager_id: departementDialog.departement.manager_id || null,
        annual_budget: departementDialog.departement.annual_budget || "",
        max_staff: departementDialog.departement.max_staff || 0,
        current_staff: departementDialog.departement.current_staff || 0,
        order: departementDialog.departement.order || 0,
      });
    } else if (departementDialog.open && departementDialog.mode === "create") {
      setNewDepartement({
        name: "",
        code: "",
        description: "",
        parent_id: departementDialog.parent ? departementDialog.parent.id : null,
        manager_id: null,
        annual_budget: "",
        max_staff: "",
        current_staff: 0,
        order: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departementDialog]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }
    const results = [];
    function searchTree(node, path) {
      const match =
        node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (node.code && node.code.toLowerCase().includes(searchTerm.toLowerCase()));
      if (match) {
        results.push({ ...node, path: [...path, node] });
      }
      if (node.enfants) {
        node.enfants.forEach((enfant) => searchTree(enfant, [...path, node]));
      }
    }
    departements.forEach((root) => searchTree(root, []));
    setSearchResults(results);
    setDropdownOpen(true);
  }, [searchTerm, departements]);

  // Fermer le popover si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Fonction pour créer la liste des départements parents possibles
  const getDepartementsParentOptions = () => {
    const flattenDepartements = (depts, level = 0) => {
      let result = [];
      for (const dept of depts) {
        result.push({
          id: dept.id,
          name: dept.name,
          level: level,
          displayName: "  ".repeat(level) + dept.name + (dept.code ? ` (${dept.code})` : ""),
        });
        if (dept.enfants && dept.enfants.length > 0) {
          result = result.concat(flattenDepartements(dept.enfants, level + 1));
        }
      }
      return result;
    };

    return flattenDepartements(departements);
  };

  const toggleExpand = (departementId) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(departementId)) {
        newSet.delete(departementId);
      } else {
        newSet.add(departementId);
      }
      return newSet;
    });
  };

  const handleSaveDepartement = async () => {
    try {
      // Validation des champs requis
      if (!newDepartement.name || !newDepartement.name.trim()) {
        toast.error(t("organigramme.toasts.validationError"), { description: t("organigramme.toasts.nameRequired") });
        return;
      }

      // Utiliser parent_id du newDepartement (qui peut être modifié par le select)
      // ou fallback sur departementDialog.parent
      const finalParentId = newDepartement.parent_id || (departementDialog.parent ? departementDialog.parent.id : null);

      const deptPayload = {
        ...newDepartement,
        parent_id: finalParentId,
        annual_budget: newDepartement.annual_budget ? parseFloat(newDepartement.annual_budget) : null,
        max_staff: newDepartement.max_staff ? parseInt(newDepartement.max_staff) : null,
        order: newDepartement.order || 0,
      };

      let response;
      if (departementDialog.mode === "edit" && departementDialog.departement) {
        // MODIFICATION
        response = await organigrammeAPI.updateDepartement(departementDialog.departement.id, deptPayload);
      } else {
        // CRÉATION
        response = await organigrammeAPI.createDepartement(deptPayload);
      }

      if (response.data.success) {
        toast.success(departementDialog.mode === "edit" ? t("organigramme.toasts.deptUpdated") : t("organigramme.toasts.deptCreated"), { description: response.data.message });

        setDepartementDialog({ open: false, departement: null, mode: "create", parent: null });
        setNewDepartement({
          name: "",
          code: "",
          description: "",
          parent_id: null,
          manager_id: null,
          annual_budget: "",
          max_staff: 0,
          current_staff: 0,
          order: 0,
        });
        refreshOrganigramme();

        // Rafraîchir le compteur dans le contexte global
        await refreshDepartementStats();
      }
    } catch (error) {
      toast.error(t("common.error"), { description: error.response?.data?.detail || error.message || t("organigramme.toasts.saveError") });
    }
  };

  const handleDeleteDepartement = async (departement) => {
    try {
      // === APPEL API BACKEND ===
      const response = await organigrammeAPI.deleteDepartement(departement.id);

      if (response.data.success) {
        // Succès - afficher le message de confirmation
        toast.success(t("organigramme.toasts.deptDeleted"), { description: response.data.message });

        // Fermer le dialog
        setDeleteDialog({ open: false, departement: null });

        // Recharger l'organigramme pour refléter les changements
        refreshOrganigramme();

        // Rafraîchir le compteur dans le contexte global
        await refreshDepartementStats();
      }
    } catch (error) {

      // Gestion spécialisée des erreurs selon le code HTTP
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;

      let title = t("organigramme.toasts.deleteErrorTitle");
      let description = t("organigramme.toasts.unexpectedError");

      switch (status) {
        case 404:
          title = t("organigramme.toasts.notFoundTitle");
          description = t("organigramme.toasts.notFoundDesc");
          break;

        case 409:
          // Conflit métier (employés affectés, sous-départements, etc.)
          title = t("organigramme.toasts.deleteImpossibleTitle");
          description = detail; // Message détaillé du backend
          break;

        case 500:
          title = t("organigramme.toasts.technicalErrorTitle");
          description = t("organigramme.toasts.serverError");
          break;

        default:
          title = t("common.error");
          description = detail || t("organigramme.toasts.deleteError");
      }

      toast.error(title, { description: description });

      // Fermer le dialog même en cas d'erreur (sauf si c'est une erreur technique majeure)
      if (status !== 500) {
        setDeleteDialog({ open: false, departement: null });
      }
    }
  };

  const handleDragStart = (e, departement) => {
    setDraggedItem(departement);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, targetDepartement, dropZone = "center") => {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetDepartement.id) {
      setDraggedItem(null);
      return;
    }

    // Différents types de drop selon la zone
    switch (dropZone) {
      case "before":
        return handleReorderDrop(e, targetDepartement, "before");
      case "after":
        return handleReorderDrop(e, targetDepartement, "after");
      case "center":
      default:
        return handleParentDrop(e, targetDepartement);
    }
  };

  const handleParentDrop = async (e, targetDepartement) => {
    // Empêcher de déplacer un parent vers ses enfants (éviter les boucles)
    const isDescendant = (dept, potentialParentId) => {
      if (dept.id === potentialParentId) return true;
      if (dept.children) {
        return dept.children.some((child) => isDescendant(child, potentialParentId));
      }
      return false;
    };

    if (isDescendant(draggedItem, targetDepartement.id)) {
      toast.error(t("organigramme.toasts.moveImpossibleTitle"), { description: t("organigramme.toasts.moveIntoChild") });
      setDraggedItem(null);
      return;
    }

    try {
      // Déplacement hiérarchique effectué

      // Mettre à jour côté backend
      const updateData = {
        parent_id: targetDepartement.id,
      };

      const response = await organigrammeAPI.updateDepartement(draggedItem.id, updateData);

      if (response.data.success) {
        toast.success(t("organigramme.toasts.deptMoved"), { description: t("organigramme.toasts.movedUnder", { name: draggedItem.name, target: targetDepartement.name }) });

        // Recharger l'organigramme pour refléter les changements
        refreshOrganigramme();

        // Rafraîchir le compteur dans le contexte global
        await refreshDepartementStats();
      }
    } catch (error) {
      toast.error(t("common.error"), { description: error.response?.data?.detail || t("organigramme.toasts.moveError") });
    } finally {
      setDraggedItem(null);
    }
  };

  const handleReorderDrop = async (e, targetDepartement, position = "after") => {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetDepartement.id) {
      setDraggedItem(null);
      return;
    }

    try {
      // Pour les départements racines, parent_id est null
      const targetParentId = targetDepartement.parent_id;

      // Obtenir tous les départements du même niveau (même parent_id)
      // Pour les racines: parent_id === null
      // Pour les sous-départements: parent_id === id_parent
      const siblings = departements.filter((dept) => {
        // Comparaison stricte pour gérer null === null
        return dept.parent_id === targetParentId;
      });

      // Si draggedItem n'est pas dans les siblings, l'ajouter (changement de niveau)
      const isDraggedInSiblings = siblings.some((s) => s.id === draggedItem.id);
      if (!isDraggedInSiblings) {
        siblings.push({ ...draggedItem, parent_id: targetParentId });
      }

      // Trier par ordre actuel pour avoir la séquence correcte
      siblings.sort((a, b) => a.order - b.order);

      // Trouver l'index de la cible
      const targetIndex = siblings.findIndex((dept) => dept.id === targetDepartement.id);

      // Calculer le nouvel index pour draggedItem
      let newIndex;
      if (position === "before") {
        newIndex = targetIndex;
      } else {
        // 'after'
        newIndex = targetIndex + 1;
      }

      // Exclure draggedItem de la liste actuelle
      const reorderedList = siblings.filter((dept) => dept.id !== draggedItem.id);

      // Insérer draggedItem à la nouvelle position
      reorderedList.splice(newIndex, 0, { ...draggedItem, parent_id: targetParentId });

      // Créer les données de réorganisation avec ordres séquentiels
      const reorderData = reorderedList.map((dept, index) => ({
        id: dept.id,
        order: index,
        // Si c'est draggedItem et qu'il change de parent, inclure parent_id
        ...(dept.id === draggedItem.id && draggedItem.parent_id !== targetParentId && { parent_id: targetParentId }),
      }));

      // Envoyer au backend
      const response = await organigrammeAPI.reorderDepartements(reorderData);

      if (response.data.success) {
        toast.success(t("organigramme.toasts.reorderSuccess"), { description: t("organigramme.toasts.repositioned", { name: draggedItem.name }) });

        // Recharger l'organigramme pour refléter les changements
        refreshOrganigramme();

        // Rafraîchir le compteur dans le contexte global
        await refreshDepartementStats();
      }
    } catch (error) {
      toast.error(t("common.error"), { description: error.response?.data?.detail || t("organigramme.toasts.reorderError") });
    } finally {
      setDraggedItem(null);
    }
  };

  const handleDropToRoot = async (e) => {
    e.preventDefault();

    if (!draggedItem) {
      return;
    }

    // Si c'est déjà un département racine, ne rien faire
    if (!draggedItem.parent_id) {
      toast.success(t("organigramme.toasts.info"), { description: t("organigramme.toasts.alreadyRoot", { name: draggedItem.name }) });
      setDraggedItem(null);
      return;
    }

    try {
      // Mettre à jour côté backend - parent_id devient null
      const updateData = {
        parent_id: null,
      };

      const response = await organigrammeAPI.updateDepartement(draggedItem.id, updateData);

      if (response.data.success) {
        toast.success(t("organigramme.toasts.deptRaised"), { description: t("organigramme.toasts.nowRoot", { name: draggedItem.name }) });

        // Recharger l'organigramme pour refléter les changements
        refreshOrganigramme();
      }
    } catch (error) {
      toast.error(t("common.error"), { description: error.response?.data?.detail || t("organigramme.toasts.raiseError") });
    } finally {
      setDraggedItem(null);
    }
  };

  // Calcul du nombre total de départements (récursif si arbre imbriqué)
  const countDepartements = useCallback((nodes) => {
    if (!nodes) return 0;
    let count = 0;
    for (const node of nodes) {
      count += 1;
      if (node.enfants && node.enfants.length > 0) {
        count += countDepartements(node.enfants);
      }
    }
    return count;
  }, []);

  useEffect(() => {
    refreshDepartementStats(countDepartements(departements));
  }, [departements, refreshDepartementStats, countDepartements]);


  const nodeProps = {
    expandedNodes, toggleExpand, selectedDepartement, setSelectedDepartement,
    draggedItem, handleDragStart, handleDragOver, handleDrop,
    setDepartementDialog, setDeleteDialog, employees, loadOrganigramme: refreshOrganigramme, toast,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="space-y-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600/20 border-t-purple-600 mx-auto"></div>
          <p className="text-lg font-medium text-muted-foreground">{t("organigramme.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OrganigrammeSearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
        searchResults={searchResults}
        inputRef={inputRef}
        selectedDepartement={selectedDepartement}
        setSelectedDepartement={setSelectedDepartement}
        departements={departements}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("organigramme.header.title")}</h1>
          <p className="text-muted-foreground">{t("organigramme.header.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setDepartementDialog({ open: true, departement: null, mode: "create", parent: null })}>
            <Plus className="mr-2 h-4 w-4" />
            {t("organigramme.header.newDept")}
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {t("common.export")}
          </Button>
        </div>
      </div>

      <OrganigrammeStatsCards stats={stats} />

      <OrganigrammeTreeCard
        loading={loading}
        draggedItem={draggedItem}
        companyPrimaryColor={companyPrimaryColor}
        companyLogoUrl={companyLogoUrl}
        companyLegalName={companyLegalName}
        handleDragOver={handleDragOver}
        handleDropToRoot={handleDropToRoot}
        departements={departements}
        nodeProps={nodeProps}
      />

      <DepartementFormDialog
        departementDialog={departementDialog}
        setDepartementDialog={setDepartementDialog}
        newDepartement={newDepartement}
        setNewDepartement={setNewDepartement}
        getDepartementsParentOptions={getDepartementsParentOptions}
        employees={employees}
        handleSaveDepartement={handleSaveDepartement}
      />

      <DeleteDepartementDialog
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        handleDeleteDepartement={handleDeleteDepartement}
      />
    </div>
  );
};

export default Organigramme;
